import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error (backend down)
    if (!error.response) {
      console.warn('⚠️ Network error - backend might be unavailable');
      return Promise.reject({
        networkError: true,
        message: 'Cannot connect to server. Please check your connection.'
      });
    }

    // Handle 401/403 - Invalid or expired token
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.warn('🔒 Authentication error - clearing tokens');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      
      // Dispatch custom event for auth state update
      window.dispatchEvent(new CustomEvent('auth:token-invalid'));
      
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export interface UserProfile {
  user_id: number;
  location?: string;
  bio?: string;
  profile_image?: string;
  company_size?: '1-10' | '11-50' | '51-200' | '201-1000' | '1000+';
  linkedin_url?: string;
  website_url?: string;
  birth_date?: string;
  gender?: 'male' | 'female';
}

export interface User {
  id: number;
  email: string;
  user_type: 'employee' | 'employer' | 'admin' | 'job_seeker';
  status: 'active' | 'inactive' | 'suspended';
  email_verified: boolean;
  first_name?: string;
  last_name?: string;
  phone?: string;
  company_name?: string;
  company_code?: string;
  company_activity?: string;
  contact_person?: string;
  website_url?: string;
  is_recruitment_agency?: boolean;
  google_id?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
  profile?: UserProfile;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    user: User;
    token: string;
  };
}

export interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
  userType: 'employee' | 'employer';
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
  companyCode?: string;
  companyActivity?: string;
  contactPerson?: string;
  isRecruitmentAgency?: boolean;
  acceptTerms: boolean;
}

export interface LoginData {
  email: string;
  password: string;
}

class AuthService {
  // Translate error messages to Lithuanian
  private translateError(message: string): string {
    const translations: Record<string, string> = {
      'User with this email already exists': 'Vartotojas su šiuo el. paštu jau egzistuoja',
      'Validation failed': 'Neteisingi duomenys',
      'Email is required': 'El. paštas privalomas',
      'Invalid email format': 'Neteisingas el. pašto formatas',
      'Password is required': 'Slaptažodis privalomas',
      'Password must be at least 8 characters with uppercase, lowercase, and number': 'Slaptažodis turi būti bent 8 simbolių su didžiąja, mažąja raide ir skaičiumi',
      'Passwords do not match': 'Slaptažodžiai nesutampa',
      'User type is required': 'Vartotojo tipas privalomas',
      'You must accept the terms and conditions': 'Privaloma sutikti su naudojimo taisyklėmis',
      'Invalid email or password': 'Neteisingas el. paštas arba slaptažodis',
      'Account is not active. Please contact support.': 'Paskyra neaktyvi. Susisiekite su mumis.',
      'Registration failed': 'Registracija nepavyko',
      'Login failed': 'Prisijungti nepavyko',
    };
    return translations[message] || message;
  }

  // Register new user
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/register', data);
      
      if (response.data.success && response.data.data) {
        // Store token and user data
        localStorage.setItem('auth_token', response.data.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.data.user));
      }
      
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Registration failed';
      return {
        success: false,
        message: this.translateError(message)
      };
    }
  }

  // Login user
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/login', data);
      
      if (response.data.success && response.data.data) {
        localStorage.setItem('auth_token', response.data.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.data.user));
      }
      
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Login failed';
      return {
        success: false,
        message: this.translateError(message)
      };
    }
  }

  // Google OAuth login/register
  async googleLogin(credential: string, userType?: string): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/google', { credential, userType });
      
      if (response.data.success && response.data.data) {
        localStorage.setItem('auth_token', response.data.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.data.user));
      }
      
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Google prisijungimas nepavyko';
      return {
        success: false,
        message: this.translateError(message)
      };
    }
  }

  // Get current user profile
  async getProfile(): Promise<{ 
    success: boolean; 
    user?: User; 
    message?: string; 
    networkError?: boolean;
  }> {
    try {
      const response = await api.get('/profile');
      
      if (response.data.success && response.data.data) {
        // Update stored user data
        localStorage.setItem('user_data', JSON.stringify(response.data.data.user));
        return {
          success: true,
          user: response.data.data.user
        };
      }
      
      return {
        success: false,
        message: response.data.message || 'Failed to get profile'
      };
    } catch (error: any) {
      // Check if it's a network error (backend down)
      if (error.networkError || !error.response) {
        console.warn('⚠️ Backend unavailable during profile fetch');
        return {
          success: false,
          networkError: true,
          message: 'Backend temporarily unavailable'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to get profile'
      };
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
    }
  }

  // Check if token is expired
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const decoded: any = jwtDecode(token);
      if (!decoded.exp) return false; // No expiration set
      
      // Check if token expires in less than 5 minutes (add buffer)
      const expirationTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
      
      const isExpired = expirationTime - currentTime < bufferTime;
      
      if (isExpired) {
        console.warn('⏰ Token is expired or expiring soon');
      }
      
      return isExpired;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true; // Treat as expired if can't decode
    }
  }

  // Check if user is authenticated (with token expiration check)
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    
    if (!token || !userData) {
      return false;
    }
    
    // Check if token is expired
    if (this.isTokenExpired()) {
      console.warn('🔒 Token expired, clearing auth state');
      this.logout(); // Auto-cleanup
      return false;
    }
    
    return true;
  }

  // Get current user from local storage
  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Get auth token
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  // Refresh authentication token
  async refreshToken(): Promise<boolean> {
    try {
      console.log('🔄 Refreshing authentication token...');
      const response = await api.post('/auth/refresh');
      
      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
        console.log('✅ Token refreshed successfully');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      return false;
    }
  }
}

const authService = new AuthService();
export default authService;
